import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Package, Image as ImageIcon, Grid, List, Eye } from 'lucide-react';

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
}

interface StaffProductsListProps {
  products: Product[];
}

export const StaffProductsList = ({ products }: StaffProductsListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Filter products based on search query
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.barcode.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.catalogName && product.catalogName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const ProductCard = ({ product }: { product: Product }) => (
    <Card key={product.id} className="overflow-hidden hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Product Photo */}
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative">
            {product.photos && product.photos.length > 0 ? (
              <img
                src={product.photos[0]}
                alt={product.name}
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                onClick={() => handleImageClick(product.photos[0])}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
            )}
            {product.photos && product.photos.length > 1 && (
              <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                +{product.photos.length - 1}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-2">
            <h3 className="font-semibold text-sm leading-tight line-clamp-2">
              {product.name}
            </h3>
            
            <div className="text-xs text-gray-600 space-y-1">
              <div className="font-mono">{product.barcode}</div>
              <div className="flex items-center justify-between">
                <span>{product.boxes} boxes</span>
                <span>{product.unitsPerBox} units/box</span>
              </div>
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap gap-1">
              <Badge 
                variant={product.posStatus === 'found' ? 'default' : 'secondary'} 
                className="text-xs"
              >
                {product.posStatus === 'found' ? 'In System' : 'New Product'}
              </Badge>
              {product.hasCredit && (
                <Badge variant="destructive" className="text-xs">
                  Credit
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ProductRow = ({ product }: { product: Product }) => (
    <div key={product.id} className="flex items-center gap-4 p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow">
      {/* Photo */}
      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
        {product.photos && product.photos.length > 0 ? (
          <img
            src={product.photos[0]}
            alt={product.name}
            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleImageClick(product.photos[0])}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-6 h-6 text-gray-400" />
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-sm truncate">{product.name}</h3>
        <p className="text-xs text-gray-600 font-mono">{product.barcode}</p>
      </div>

      {/* Quantities */}
      <div className="text-right text-sm text-gray-600 flex-shrink-0">
        <div>{product.boxes} boxes</div>
        <div className="text-xs">{product.unitsPerBox} units/box</div>
      </div>

      {/* Status Badges */}
      <div className="flex flex-col gap-1 flex-shrink-0">
        <Badge 
          variant={product.posStatus === 'found' ? 'default' : 'secondary'} 
          className="text-xs"
        >
          {product.posStatus === 'found' ? 'In System' : 'New'}
        </Badge>
        {product.hasCredit && (
          <Badge variant="destructive" className="text-xs">
            Credit
          </Badge>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {filteredProducts.length} of {products.length} products
          </span>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              onClick={() => setViewMode("grid")}
              size="sm"
              className="h-8 w-8 p-0"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "table" ? "default" : "ghost"}
              onClick={() => setViewMode("table")}
              size="sm"
              className="h-8 w-8 p-0"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Products Display */}
      {filteredProducts.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-8">
            <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              {products.length === 0 ? "No Products Found" : "No Products Match Search"}
            </h3>
            <p className="text-sm text-gray-500">
              {products.length === 0 
                ? "This order doesn't have any products scanned yet."
                : "Try a different search term or clear your search."
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            : "space-y-2"
        }>
          {filteredProducts.map(product => 
            viewMode === 'grid' 
              ? <ProductCard key={product.id} product={product} />
              : <ProductRow key={product.id} product={product} />
          )}
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={selectedImage}
              alt="Product image"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-4 right-4"
              onClick={() => setSelectedImage(null)}
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};